import { spawn, spawnSync, type ChildProcess } from 'node:child_process';
import chalk from 'chalk';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { error, info, success } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

interface HostDetail {
  id: string;
  name: string;
  status: string;
  framework: string;
  provision_source: 'self' | 'augmented-ec2' | null;
  ec2_instance_id: string | null;
  ec2_region: string | null;
  claude_auth_mode: string | null;
  claude_auth_status: string | null;
  claude_auth_expires_at: string | null;
}

/**
 * agt host pair <name>
 *
 * Orchestrates a Claude Code `/login` flow on a remote EC2 host by:
 *   1. Looking up the host's EC2 instance ID via the API
 *   2. Starting an SSM port-forward tunnel on a chosen local port
 *   3. Opening an interactive SSM shell session on the remote host
 *
 * The operator then runs `claude /login` inside the shell, clicks the URL
 * Claude Code prints, completes OAuth in the browser — the callback lands on
 * localhost:<port>, which tunnels through SSM to the host's loopback where
 * Claude Code is listening.
 *
 * When the operator exits the shell (Ctrl+D), the port-forward tunnel is
 * torn down.
 *
 * Prerequisites on the operator's machine:
 *   - AWS CLI v2 with credentials scoped to the host AWS account
 *   - session-manager-plugin installed
 *     (brew install --cask session-manager-plugin)
 *
 * Note on port matching:
 *   Claude Code picks an OAuth callback port dynamically. For the forwarded
 *   port to receive the callback, ensure Claude Code binds to the same port
 *   as --port (or set CLAUDE_CODE_OAUTH_PORT on the host). If the ports
 *   don't match, the OAuth callback won't reach the host.
 */
export async function hostPairCommand(
  name: string,
  opts: { port?: string; noShell?: boolean }
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const localPort = Number(opts.port ?? '54545');

  if (!Number.isInteger(localPort) || localPort < 1024 || localPort > 65535) {
    error('--port must be an integer between 1024 and 65535');
    process.exitCode = 1;
    return;
  }

  // Fetch host detail — we need the EC2 instance ID
  let host: HostDetail;
  try {
    const data = await api.get<{ host: HostDetail }>(`/hosts/${encodeURIComponent(name)}`);
    host = data.host;
  } catch (err) {
    if (json) jsonOutput({ ok: false, error: (err as Error).message });
    else error(`Failed to look up host: ${(err as Error).message}`);
    process.exitCode = 1;
    return;
  }

  if (host.provision_source !== 'augmented-ec2') {
    const msg = `Host "${name}" is ${host.provision_source ?? 'self'}-provisioned, not an Augmented-managed EC2. ` +
      `agt host pair is only for EC2 hosts provisioned through the Augmented API.`;
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  if (!host.ec2_instance_id) {
    const msg = `Host "${name}" has no EC2 instance ID yet. Provision it first with \`agt host provision\`.`;
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  const region = host.ec2_region ?? process.env.AWS_REGION ?? 'us-east-2';
  const instanceId = host.ec2_instance_id;

  // Preflight: aws + session-manager-plugin
  const preflightErr = preflightAws();
  if (preflightErr) {
    if (json) jsonOutput({ ok: false, error: preflightErr });
    else error(preflightErr);
    process.exitCode = 1;
    return;
  }

  if (json) {
    // JSON mode: emit the tunnel parameters, caller orchestrates the shell
    jsonOutput({
      ok: true,
      host: { name: host.name, id: host.id, instance_id: instanceId, region },
      port_forward: {
        command: 'aws',
        args: [
          'ssm', 'start-session',
          '--region', region,
          '--target', instanceId,
          '--document-name', 'AWS-StartPortForwardingSession',
          '--parameters', JSON.stringify({ portNumber: [String(localPort)], localPortNumber: [String(localPort)] }),
        ],
      },
      shell: {
        command: 'aws',
        args: ['ssm', 'start-session', '--region', region, '--target', instanceId],
      },
    });
    return;
  }

  info(`Pairing Claude Code on ${chalk.bold(name)} (${instanceId}, ${region})`);
  info(`Local port ${chalk.cyan(String(localPort))} will be forwarded to the host.`);
  console.log();
  console.log(chalk.dim('In the shell that opens, run:'));
  console.log(`  ${chalk.cyan(`CLAUDE_CODE_OAUTH_PORT=${localPort} claude /login`)}`);
  console.log(chalk.dim('Then click the printed URL on this machine. The OAuth callback'));
  console.log(chalk.dim(`will reach Claude Code via the port-forward tunnel.`));
  console.log();
  console.log(chalk.dim('Exit the shell (Ctrl+D) when Claude Code reports successful login.'));
  console.log();

  // Start the port-forward tunnel in the background
  const tunnel = spawn(
    'aws',
    [
      'ssm', 'start-session',
      '--region', region,
      '--target', instanceId,
      '--document-name', 'AWS-StartPortForwardingSession',
      '--parameters',
      JSON.stringify({ portNumber: [String(localPort)], localPortNumber: [String(localPort)] }),
    ],
    { stdio: ['ignore', 'pipe', 'pipe'] }
  );

  // Surface tunnel errors but don't crash — often it reports "Waiting for connections..."
  tunnel.stderr?.on('data', (buf) => {
    const line = buf.toString().trim();
    if (line.startsWith('An error occurred')) {
      console.error(chalk.red(`[tunnel] ${line}`));
    }
  });

  // Give the tunnel ~1.5s to establish before launching the interactive shell
  await new Promise((r) => setTimeout(r, 1500));

  if (tunnel.exitCode !== null) {
    error('Port-forward tunnel failed to start. Check AWS credentials and session-manager-plugin.');
    process.exitCode = 1;
    return;
  }

  // Launch the interactive shell — inherits stdio so the user gets a real TTY.
  // --no-shell: keep the tunnel up in the foreground and wait for SIGINT.
  if (opts.noShell) {
    console.log(chalk.dim('--no-shell set; tunnel is running. Press Ctrl+C to exit.'));
    await new Promise<void>((resolve) => {
      const onSignal = () => {
        terminate(tunnel);
        resolve();
      };
      process.once('SIGINT', onSignal);
      process.once('SIGTERM', onSignal);
    });
  } else {
    await runInteractive('aws', ['ssm', 'start-session', '--region', region, '--target', instanceId]);
    // User exited the shell — tear the tunnel down
    terminate(tunnel);
  }

  success('Pair session ended.');
}

function preflightAws(): string | null {
  const aws = spawnSync('aws', ['--version'], { stdio: 'ignore' });
  if (aws.status !== 0) {
    return 'AWS CLI not found. Install: https://aws.amazon.com/cli/';
  }
  const plugin = spawnSync('session-manager-plugin', [], { stdio: 'ignore' });
  // session-manager-plugin exits non-zero when called without a session, but it exists if the spawn succeeded
  if (plugin.error) {
    return 'session-manager-plugin not found. Install: brew install --cask session-manager-plugin';
  }
  return null;
}

function runInteractive(cmd: string, args: string[]): Promise<number> {
  return new Promise((resolve) => {
    const child = spawn(cmd, args, { stdio: 'inherit' });
    child.on('exit', (code) => resolve(code ?? 0));
    child.on('error', () => resolve(1));
  });
}

function terminate(child: ChildProcess) {
  if (child.exitCode !== null) return;
  try {
    child.kill('SIGTERM');
  } catch {
    // process may already be gone
  }
}
